import { useEffect, useState } from "react";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Coins, Check, Search, Save, Lock } from "lucide-react";
import { toast } from "sonner";

export default function HppList() {
  const { user } = useAuth();
  const isOwner = user?.is_owner;
  const [items, setItems] = useState([]);
  const [edits, setEdits] = useState({});
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const [locking, setLocking] = useState(false);
  const [lockMonth, setLockMonth] = useState("");

  const load = () => api.get("/items").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const lockHpp = async () => {
    const scope = lockMonth ? `nota bulan ${lockMonth}` : "SEMUA nota";
    if (!window.confirm(`Kunci HPP untuk ${scope}? HPP terkini akan dibekukan ke nota tersebut, sehingga profitnya tidak berubah lagi meski HPP diedit. Nota yang HPP-nya sudah dikunci tidak akan tersentuh.`)) return;
    setLocking(true);
    try {
      const { data } = await api.post(`/sales/lock-hpp${lockMonth ? `?month=${lockMonth}` : ""}`);
      toast.success(`Selesai: ${data.updated_notas} nota, ${data.locked_lines} baris dikunci (${data.month})`);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setLocking(false); }
  };

  const save = async (i) => {
    const val = edits[i.id];
    if (val === undefined) return;
    try {
      await api.put(`/items/${i.id}`, { buy_price: Number(val) });
      toast.success("HPP disimpan");
      setEdits((s) => { const c = { ...s }; delete c[i.id]; return c; });
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const changedCount = Object.keys(edits).length;

  const saveAll = async () => {
    const payload = Object.entries(edits).map(([id, v]) => ({ id, buy_price: Number(v) }));
    if (!payload.length) return;
    setSaving(true);
    try {
      const { data } = await api.put("/hpp/bulk", { items: payload });
      toast.success(`${data.updated} HPP tersimpan`);
      setEdits({}); load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const filtered = items.filter((i) => (i.name + (i.warna || "")).toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-5 animate-fadeup">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-extrabold">HPP</h1>
          <p className="text-[#52525B] text-sm">Harga Pokok / modal per barang — isi cepat lalu simpan sekaligus</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isOwner && (
            <div className="flex items-center gap-2">
              <input data-testid="lock-hpp-month" type="month" value={lockMonth} onChange={(e) => setLockMonth(e.target.value)} title="Kosongkan = semua bulan" className="nb-input w-auto" />
              <button data-testid="lock-hpp-btn" onClick={lockHpp} disabled={locking} className="nb-btn px-4 py-2.5 flex items-center gap-2 bg-white text-[#0A0A0A] disabled:opacity-60">
                <Lock className="w-5 h-5" strokeWidth={2.5} /> {locking ? "Mengunci…" : lockMonth ? "Kunci HPP Bulan Ini" : "Kunci HPP Semua"}
              </button>
            </div>
          )}
          {changedCount > 0 && (
            <button data-testid="hpp-save-all" onClick={saveAll} disabled={saving} className="nb-btn px-4 py-2.5 flex items-center gap-2 text-[#0A0A0A] disabled:opacity-60" style={{ background: "var(--masuk)" }}>
              <Save className="w-5 h-5" strokeWidth={2.5} /> {saving ? "Menyimpan…" : `Simpan Semua (${changedCount})`}
            </button>
          )}
        </div>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
        <input data-testid="hpp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk…" className="nb-input pl-9" />
      </div>

      {filtered.length === 0 ? (
        <div className="nb-card p-10 text-center text-[#52525B]"><Coins className="w-10 h-10 mx-auto mb-3" />Tidak ada produk.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
          {filtered.map((i) => {
            const val = edits[i.id] ?? (i.buy_price ?? 0);
            const changed = edits[i.id] !== undefined;
            return (
              <div key={i.id} data-testid={`hpp-card-${i.id}`} className={`nb-card p-4 ${changed ? "ring-2 ring-[var(--masuk)]" : ""}`}>
                <div className="font-display font-bold text-lg truncate">{i.name}</div>
                <span className="label-tag">{i.warna || "—"}</span>
                <div className="mt-3">
                  <label className="label-tag block mb-1">HPP (Modal)</label>
                  <div className="flex gap-2">
                    <input data-testid={`hpp-input-${i.id}`} type="number" min="0" className="nb-input" value={val} onChange={(e) => setEdits((s) => ({ ...s, [i.id]: e.target.value }))} />
                    {changed && <button data-testid={`hpp-save-${i.id}`} onClick={() => save(i)} title="Simpan barang ini" className="nb-btn px-3 shrink-0 text-[#0A0A0A]" style={{ background: "var(--primary)" }}><Check className="w-4 h-4" /></button>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
